import { useEffect } from "react";

import { Nav } from "@/components/Nav";
import { SmoothScroll } from "@/components/SmoothScroll";
import { Router, usePath } from "@/router";
import { routeFor } from "@/routes";

/**
 * The shell every route renders inside.
 *
 * The nav and the scroll island live here rather than in each page, so moving
 * between / and /contact swaps only the main content. The nav never unmounts
 * and never repaints, which is most of what makes a two-page site feel like
 * one application.
 *
 * `path` is a prop rather than something read off `window` so that the
 * prerender script can render each route in Node. It is the only concession
 * the component tree makes to being rendered twice.
 */
export function App({ path }: { path: string }) {
  return (
    <Router path={path}>
      <SmoothScroll />
      <Nav />
      <Page />
    </Router>
  );
}

function Page() {
  const path = usePath();
  const { Component, title, description } = routeFor(path);

  /*
   * The prerendered HTML already carries the correct title and description
   * for whichever page was loaded first. This only keeps them correct after a
   * client transition — without it, navigating to /contact would leave the
   * home page's title in the tab and in anything reading the DOM.
   */
  useEffect(() => {
    document.title = title;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", description);
  }, [title, description]);

  return <Component />;
}
